import { useEffect, useMemo, useState } from 'react';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money, qty as fmtQty } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useRegister } from '../screens/Pay.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, Switch } from './ui.tsx';

type Method = 'cash' | 'card' | 'other' | 'gift_card' | 'points';
interface Info {
  order: { id: string; receipt_no: number | null; name: string; total: number; closed_at: string | null };
  lines: { id: string; name: string; qty: number; unit_price: number; total: number; left_qty: number; left_total: number; modifiers: string[] }[];
  paid_by: Partial<Record<Method, number>>;
}

const METHODS: Method[] = ['cash', 'card', 'other', 'gift_card', 'points'];
const METHOD_ICON: Record<Method, IconName> = { cash: 'cash', card: 'card', other: 'receipt', gift_card: 'gift', points: 'star' };
// Gift card money goes back onto the cards it came from, points back to the customer.
const METHOD_LABEL = {
  cash: 'pay.method_cash',
  card: 'pay.method_card',
  other: 'pay.method_other',
  gift_card: 'refund.to_gift_card',
  points: 'refund.to_points',
} as const;

/** What the server will give back for `n` of a line: the last of a line takes exactly what is left. */
const partOf = (l: Info['lines'][number], n: number) => (n >= l.left_qty ? l.left_total : Math.round((l.total * n) / l.qty));

/**
 * Money back on a paid receipt. The manager picks the lines and how many, the reason, and
 * how the money goes back; it never goes back by a method the guest did not pay with.
 */
export function RefundDialog({ order, onClose, onDone }: { order: string; onClose: () => void; onDone: (refund: string) => void }) {
  const { fail, toast } = useUi();
  const { can } = useSession();
  const { registers, register } = useRegister();
  const [info, setInfo] = useState<Info | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [method, setMethod] = useState<Method | null>(null);
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    action<Info>('sales.refundable', { order }).then(setInfo, (err) => {
      fail(err);
      onClose();
    });
  }, [order]);

  const open = info?.lines.filter((l) => l.left_qty > 0) ?? [];
  const due = open.reduce((s, l) => s + (counts[l.id] ? partOf(l, counts[l.id]!) : 0), 0);
  const allChosen = open.length > 0 && open.every((l) => counts[l.id] === l.left_qty);
  const paidWith = METHODS.filter((m) => (info?.paid_by[m] ?? 0) > 0);
  const first = method ?? paidWith[0] ?? 'cash';

  // The chosen method first; what it cannot cover goes back the other ways the guest paid.
  const split = useMemo(() => {
    let left = due;
    const out: { method: Method; amount: number }[] = [];
    for (const m of [first, ...paidWith.filter((x) => x !== first)]) {
      const take = Math.min(left, info?.paid_by[m] ?? 0);
      if (take > 0) out.push({ method: m, amount: take });
      left -= take;
    }
    return out;
  }, [due, first, info]);

  const setCount = (id: string, n: number) => setCounts((c) => ({ ...c, [id]: n }));
  const chooseAll = () => setCounts(allChosen ? {} : Object.fromEntries(open.map((l) => [l.id, l.left_qty])));
  const reasons = [t('refund.reason_wrong'), t('refund.reason_complaint'), t('refund.reason_twice'), t('refund.reason_collected')];
  const session = register?.session ?? null;

  const go = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await action<{ order: string; receipt_no: number; total: number }>('restaurant.refund', {
        order,
        session: session.id,
        reason: reason.trim(),
        restock,
        lines: open.filter((l) => counts[l.id]).map((l) => ({ line: l.id, qty: counts[l.id] })),
        payments: split,
      });
      const cash = split.find((p) => p.method === 'cash')?.amount ?? 0;
      toast(cash ? t('refund.done_cash', { n: r.receipt_no, amount: money(cash) }) : t('refund.done', { n: r.receipt_no }));
      onDone(r.order);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  const title = t('refund.title', { n: info?.order.receipt_no ?? '' });
  if (info && open.length === 0) {
    return (
      <Dialog title={title} onClose={onClose} size="sm">
        <EmptyState icon="undo" title={t('refund.nothing_left')} body={t('refund.nothing_left_body')} />
      </Dialog>
    );
  }
  if (registers && !session) {
    return (
      <Dialog title={title} onClose={onClose} size="sm">
        <EmptyState icon="register" title={t('refund.no_shift')} body={t('refund.no_shift_body')} />
      </Dialog>
    );
  }

  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="undo" busy={busy} disabled={!info || !session || due <= 0 || reason.trim().length < 3} onClick={() => void go()}>
            {due > 0 ? t('refund.give_back', { amount: money(due) }) : t('refund.choose')}
          </Button>
        </>
      }
    >
      {!info ? (
        <Skeleton h={260} />
      ) : (
        <div className="refund">
          <div className="refund-head">
            <span className="muted">{t('refund.what')}</span>
            <Button variant="ghost" size="sm" icon={allChosen ? 'x' : 'check'} onClick={chooseAll}>
              {allChosen ? t('refund.none') : t('refund.all')}
            </Button>
          </div>
          <ul className="refund-lines">
            {info.lines.map((l) => {
              const n = counts[l.id] ?? 0;
              const gone = l.left_qty <= 0;
              return (
                <li key={l.id} className={`refund-line${n ? ' chosen' : ''}${gone ? ' gone' : ''}`}>
                  <span className="refund-name">
                    <strong>{l.name}</strong>
                    <span className="muted">
                      {gone
                        ? t('refund.all_back')
                        : l.left_qty === l.qty
                          ? t('refund.sold', { qty: fmtQty(l.qty), price: money(l.unit_price) })
                          : t('refund.left', { left: fmtQty(l.left_qty), qty: fmtQty(l.qty) })}
                      {l.modifiers.length ? ` · ${l.modifiers.join(', ')}` : ''}
                    </span>
                  </span>
                  {gone ? null : (
                    <>
                      <div className="stepper" role="group" aria-label={t('refund.qty_of', { name: l.name })}>
                        <IconButton icon="minus" label={t('common.less')} disabled={n <= 0} onClick={() => setCount(l.id, Math.max(0, n - 1))} />
                        <span className="stepper-value">{fmtQty(n)}</span>
                        <IconButton icon="plus" label={t('common.more')} disabled={n >= l.left_qty} onClick={() => setCount(l.id, Math.min(l.left_qty, n + 1))} />
                      </div>
                      <span className="refund-amount tabular">{n ? `−${money(partOf(l, n))}` : ''}</span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="refund-method">
            <span className="field-label">{t('refund.how')}</span>
            <div className="segmented" role="radiogroup" aria-label={t('refund.how')}>
              {paidWith.map((m) => (
                <button key={m} role="radio" aria-checked={first === m} className={first === m ? 'active' : ''} onClick={() => setMethod(m)}>
                  <Icon name={METHOD_ICON[m]} size={18} /> {t(METHOD_LABEL[m])}
                </button>
              ))}
            </div>
            {split.length > 1 || (split[0] && split[0].method !== first) ? (
              <p className="refund-split muted">
                <Icon name="split" size={16} /> {split.map((p) => `${t(METHOD_LABEL[p.method])} ${money(p.amount)}`).join(' · ')}
              </p>
            ) : null}
          </div>

          <Field label={t('refund.why')}>
            <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder={t('refund.why_placeholder')} />
          </Field>
          <div className="chips">
            {reasons.map((c) => (
              <button key={c} className={`chip${reason === c ? ' active' : ''}`} onClick={() => setReason(c)}>
                {c}
              </button>
            ))}
          </div>

          {can('inventory.view') ? (
            <div className="refund-restock">
              <span>
                <strong>{t('refund.restock')}</strong>
                <span className="muted">{t('refund.restock_hint')}</span>
              </span>
              <Switch checked={restock} onChange={setRestock} label={t('refund.restock')} />
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
